import type { OnDestroy } from '@angular/core'
import { DestroyRef, Injectable, NgZone, computed, inject } from '@angular/core'
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AppEvent, AppService } from '../../../app.service'
import type { Subscription } from 'rxjs'
import { EMPTY, catchError, filter, from, switchMap, tap } from 'rxjs'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { apiEndpoint } from '@awork/environments/environment'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import getLanguage from '@awork/_shared/functions/get-language'
import { ToastService } from '../../../shared/services/toast-service/toast.service'
import { HapticService } from '../../../shared/services/haptic-service/haptic.service'
import { NotificationService } from '@awork/framework/services/notification-service/notification.service'
import { AppStore } from '@awork/core/state/app.store'
import { AppQuery } from '@awork/core/state/app.query'
import { AutoUnsubscribe } from '@awork/_shared/decorators/auto-unsubscribe'
import { Device } from '@capacitor/device'
import type { Notification } from '@awork/framework/models/notification.model'
import { SignalService } from '@awork/_shared/services/signal-service/signal.service'
import type { NotificationSetting } from '@awork/_shared/models/notification-setting.model'
import { Channel } from '@awork/_shared/models/notification-setting.model'
import { generateGUID } from '@awork/_shared/functions/guid-generator'
import type { ActionPerformed, Token } from '@capacitor/push-notifications'
import { PushNotifications } from '@capacitor/push-notifications'
import { NotificationNavigationService } from '../notification-navigation-service/notification-navigation.service'

import { NotificationEvents } from '@awork/framework/models/notification.types'

interface RegistrationEntity {
  registrationId?: string
}

@AutoUnsubscribe()
@Injectable({
  providedIn: 'root'
})
export class PushNotificationService implements OnDestroy {
  private readonly navigation = inject(NotificationNavigationService)
  private readonly haptics = inject(HapticService)
  private readonly workspaceQuery = inject(WorkspaceQuery)
  private listenersInitialized = false
  private apiClient = inject(ApiClient)
  private appService = inject(AppService)
  private toastService = inject(ToastService)
  private appStore = inject(AppStore)
  private appQuery = inject(AppQuery)
  private notificationService = inject(NotificationService)
  private ngZone = inject(NgZone)
  private signalService = inject(SignalService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly workspace = this.workspaceQuery.queryCurrentWorkspace()
  private readonly workspaceId = computed(() => this.workspace()?.id)

  private readonly url: string
  protected subscriptions: Subscription[] = []
  private notificationSettings: NotificationSetting[]
  private settingsWorkspaceId?: string

  constructor() {
    this.url = `${apiEndpoint}/me/pushnotifications/`

    // subscribe to app events
    this.subscriptions.push(
      this.appService.events$.subscribe(event => {
        if (event.type === AppEvent.Started) {
          this.initPushNotificationsListeners()
          this.initNotifications()
        } else if (event.type === AppEvent.LoggedIn) {
          this.registerPushNotifications()
        }
      })
    )

    this.getNotificationSettings()
  }

  ngOnDestroy() {}

  /**
   * Subscribes to notifications coming from WS to show them as toasts
   */
  private initNotifications(): void {
    this.subscriptions.push(
      this.signalService
        .getSignal('notification')
        .pipe(
          // Only new notifications show a toast. Updates, such as marking as seen, must not show it again
          filter(signal => signal.operationType === 'added'),
          // Filter by notification settings
          filter(signal => {
            const notification = signal.entity as Notification

            // Handle general comment mention case
            const mentionEvent =
              notification.event === NotificationEvents.ProjectMentioned ||
              notification.event === NotificationEvents.TaskMentioned

            const userSetting =
              this.settingsWorkspaceId === this.workspaceId() && this.notificationSettings
                ? this.notificationSettings.find(
                    setting =>
                      setting.channel === Channel.MOBILE &&
                      (setting.event === notification.event ||
                        (mentionEvent && setting.event === NotificationEvents.CommentMentioned)) &&
                      setting.enabled
                  )
                : undefined

            return !!userSetting
          }),
          // Filter by notification with description
          filter(signal => {
            const notification = signal.entity as Notification
            return !!notification.description
          })
        )
        .subscribe(signal => {
          const notification = signal.entity as Notification
          this.toastService.showNotification(notification, () => {
            this.haptics.buttonClick()
            this.navigation.open(notification)
          })
        })
    )
  }

  /**
   * Replaces notification preferences when the active workspace changes.
   */
  private getNotificationSettings(): void {
    toObservable(this.workspaceId)
      .pipe(
        switchMap(workspaceId => {
          this.notificationSettings = []
          this.settingsWorkspaceId = undefined

          if (!workspaceId) {
            return EMPTY
          }

          return this.notificationService.fetchNotificationSettings().pipe(
            tap(settings => {
              this.notificationSettings = settings
              this.settingsWorkspaceId = workspaceId
            }),
            // Keep preferences empty until a later workspace entry can load them.
            catchError(() => EMPTY)
          )
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()
  }

  /**
   * Handles the registration
   * @param {string} registrationId
   */
  private register(registrationId: string): void {
    // Get Device UniqueDeviceID to be used for Registration
    Device.getId().then(deviceId => {
      const devicePlatform = this.appService.isIos() ? 'IOS' : 'ANDROID'
      const uniqueId = deviceId?.identifier || generateGUID()

      const body = {
        handle: registrationId,
        uniqueDeviceId: uniqueId,
        platform: devicePlatform,
        language: getLanguage(true)
      }

      this.apiClient.post<RegistrationEntity>(`${this.url}register`, body).subscribe(createdRegistration => {
        this.appStore.setPushNotificationRegistrationId(createdRegistration.registrationId)
      })
    })
  }

  /**
   * Unregister from push notifications
   */
  unregister(): void {
    const apiClient = this.apiClient
    const apiUrl = this.url
    const registrationId = this.appQuery.selectPushNotificationRegistrationId()

    if (registrationId) {
      apiClient.post(`${apiUrl}${registrationId}/unregister`, {}).subscribe(() => {
        this.appStore.setPushNotificationRegistrationId(null)
      })
    }
  }

  /**
   * Adds the push notification listeners for registration and opening
   */
  initPushNotificationsListeners(): void {
    if (this.listenersInitialized) {
      return
    }

    this.listenersInitialized = true

    const registrationListener = PushNotifications.addListener('registration', (token: Token) => {
      this.ngZone.run(() => {
        this.register(token.value)
      })
    })

    const actionListener = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        this.ngZone.run(() => {
          this.navigation.openPush(notification.notification.data)
        })
      }
    )

    this.destroyRef.onDestroy(() => {
      void actionListener.then(listener => listener.remove())
      void registrationListener.then(listener => listener.remove())
    })
  }

  /**
   * Registers the device for push notifications.
   * Ask for permission if not already granted.
   */
  registerPushNotifications(): void {
    from(PushNotifications.checkPermissions()).subscribe(permissionStatus => {
      if (permissionStatus.receive === 'prompt') {
        from(PushNotifications.requestPermissions()).subscribe(permissionStatusFromPrompt => {
          if (permissionStatusFromPrompt.receive === 'granted') {
            PushNotifications.register()
          }
        })
      } else if (permissionStatus.receive === 'granted') {
        PushNotifications.register()
      }
    })
  }
}
